import { useState } from "react";
import { toast } from "sonner";
import { Send, CalendarHeart } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Inline "Notify me when it drops" CTA shown on the BookDetail page
 * when a book has no buy links yet (upcoming / pre-release).
 * Submits through the existing /api/newsletter/subscribe endpoint so
 * the reader receives the same welcome email flow.
 */
export default function PreOrderNotify({ publishDate, bookTitle }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      // The subscribe endpoint accepts an optional `name` — repurpose it as
      // the source label so the admin knows this came from a book pre-order.
      await api.post("/newsletter/subscribe", {
        email,
        name: bookTitle ? `Pre-order: ${bookTitle}` : "",
      });
      setDone(true);
      setEmail("");
      toast.success("You're on the list! A note is on its way. 💌");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not subscribe");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-3xl p-6 sm:p-7"
      style={{
        background: "linear-gradient(135deg, #FFF3F8 0%, #FFE7F2 55%, #EEE1FF 100%)",
        border: "1px solid #F0D9E8",
      }}
      data-testid="preorder-notify"
    >
      <div className="flex items-center gap-2">
        <CalendarHeart size={20} style={{ color: "#FF6B9D" }} />
        <span className="font-caveat text-2xl" style={{ color: "#FF6B9D" }}>
          coming soon
        </span>
      </div>
      <h4
        className="font-fredoka text-2xl mt-1"
        style={{ color: "#2D3055" }}
        data-testid="preorder-notify-title"
      >
        {publishDate ? `Releasing ${publishDate}` : "Releasing soon"}
      </h4>
      <p className="mt-2 text-sm font-nunito" style={{ color: "#4A4E6D" }}>
        Buy links are on the way. Drop your email and I&rsquo;ll send you a gentle nudge the day it&rsquo;s available.
      </p>

      {done ? (
        <p
          className="mt-4 font-caveat text-2xl"
          style={{ color: "#2D3055" }}
          data-testid="preorder-notify-thanks"
        >
          &#128149; You&rsquo;re on the list. Talk soon!
        </p>
      ) : (
        <form onSubmit={submit} className="mt-4 flex gap-2" data-testid="preorder-notify-form">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@lovely.com"
            className="flex-1 px-4 py-2.5 rounded-full text-sm outline-none border font-nunito"
            style={{ borderColor: "#F0D9E8", background: "#FFFFFF", color: "#2D3055" }}
            data-testid="preorder-notify-email"
          />
          <button
            type="submit"
            disabled={busy}
            className="px-4 py-2.5 rounded-full inline-flex items-center gap-1.5 font-fredoka text-sm transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
            style={{
              background: "#FF6B9D",
              color: "#FFFFFF",
              boxShadow: "0 8px 18px rgba(255,107,157,0.28)",
            }}
            data-testid="preorder-notify-submit"
          >
            {busy ? "…" : (<><Send size={14} /> Notify me</>)}
          </button>
        </form>
      )}
    </div>
  );
}
